import { useCallback, useRef, useState } from 'react';
import { ApolloError, useLazyQuery } from '@apollo/client';
import { NODE_TYPES } from 'modules/workflows/constants';
import { useJourneyAssistContext } from 'modules/workflows/context/JourneyAssistContext';
import {
  addEdges,
  deleteNodes,
  getEdges,
  getNextNodeId,
  getNodeMap,
  getSelectedAINodes,
  getSelectedNodes,
  getStartNode,
  getWorkflowState,
  importTileset,
  selectGenAINodes,
} from 'modules/workflows/ducks';
import { Edge, Node, BackendWorkflow } from 'modules/workflows/types';
import { useDispatch, useSelector } from 'react-redux';
import { useFlowChart } from 'ui/workflows/flowchart';
import {
  createEdgesMap,
  getTilesetBoundaries,
  connectEdgesToTileset,
} from 'modules/workflows/utils/journeyAssistUtils';
import { noop } from 'utils/noop';
import { hydrateTileset } from '../../modals/TilesetsModal/utils';
import { fetchGenAIJourneys, fetchGenAITileset } from '../JourneyAssist.graphql';
import { ERROR_MESSAGES, ToggleType, PromptType } from '../JourneyAssist.constants';

type Tileset = {
  nodes: Record<string, Node>;
  edges: Edge[];
  nextNodeId: number;
};

type TilesetData = Pick<BackendWorkflow, 'nodes' | 'edges' | 'notes'>;

type UseFetchAndInsertAIGeneratedTilesProps = {
  onFetchTilesError?: (err: string) => void;
  onFetchTilesSuccess?: () => void;
};

type InitialAddOrReplace = {
  initialAddToPath?: string;
  initialSourceEdges?: Partial<Edge>[];
  initialTargetEdges?: Partial<Edge>[];
};

const getNonStartTileIds = (nodes: Node[]) =>
  nodes.reduce(
    (acc: string[], x: Node) => (x.type !== NODE_TYPES.START ? [...acc, x.id] : acc),
    [],
  );

export const useFetchAndInsertAIGeneratedTiles = ({
  onFetchTilesError = noop,
  onFetchTilesSuccess = noop,
}: UseFetchAndInsertAIGeneratedTilesProps) => {
  const dispatch = useDispatch();
  const [initialWfState, setInitialWfState] = useState<Tileset>({} as Tileset);
  const [initialAddOrReplace, setInitialAddOrReplace] = useState<InitialAddOrReplace>(
    {} as InitialAddOrReplace,
  );
  const abortControllerRef = useRef(new AbortController());
  const context = {
    fetchOptions: {
      signal: abortControllerRef.current.signal,
    },
  };
  const { addToPathTileId, promptType, sourceEdges, targetEdges } = useJourneyAssistContext();

  /** Selectors */
  const wfState = useSelector(getWorkflowState);
  const nodeMap = useSelector(getNodeMap);
  const edges = useSelector(getEdges);
  const nextNodeId = useSelector(getNextNodeId);
  const startNode = useSelector(getStartNode);
  const selectedAINodes = useSelector(getSelectedAINodes);
  const selectedNodes = useSelector(getSelectedNodes);

  /** Flowchart utils */
  const { focus, relayout } = useFlowChart();

  /** Updates canvas when user fetches and deletes tiles
   * @param tileset - tileset to update canvas with, leave empty to reset to initial state
   */
  const updateAIGeneratedTiles = useCallback(
    (tileset?: Tileset) => {
      const { initialAddToPath } = initialAddOrReplace;

      const aiGeneratedNodeIds = getNonStartTileIds(selectedAINodes);
      const selectedNodeIds = getNonStartTileIds(selectedNodes);

      // First, delete any currently fetched tiles (except start tile) and edges
      if (aiGeneratedNodeIds.length) dispatch(deleteNodes(aiGeneratedNodeIds));

      // Then, delete any selected tiles (except start tile) and edges (unless when adding to path)
      if (selectedNodeIds.length && !initialAddToPath) dispatch(deleteNodes(selectedNodeIds));

      if (tileset) {
        const ids = Object.keys(tileset.nodes).map((x) => x.toString());
        // Import new tiles
        dispatch(importTileset(tileset));
        // todo: Need to re-think relayout after POC as this can only really be used for new journeys
        relayout();
        // Select new tiles
        dispatch(selectGenAINodes(ids));

        if (promptType === PromptType.NewTileset) {
          const { inputIds, outputIds } = getTilesetBoundaries(ids, createEdgesMap(tileset.edges));
          const { remappedSourceEdges, remappedTargetEdges } = connectEdgesToTileset({
            inputIds,
            outputIds,
            ...initialAddOrReplace,
          });
          dispatch(addEdges([...remappedSourceEdges, ...remappedTargetEdges]));
        }
      } else {
        // Import initial tiles/edges we set before first fetch
        dispatch(importTileset(initialWfState));
        // Focus on the start node
        focus(startNode?.id);
        // Deselect all tiles for good measure
        dispatch(selectGenAINodes([]));
      }
    },
    [addToPathTileId, selectedAINodes, startNode, initialWfState, initialAddOrReplace],
  );

  /** Parse returned tiles into a format to insert into canvas
   *  Or return an error if results are empty
   */
  const handleOnCompleted = (tilesetData: TilesetData) => {
    if (!tilesetData?.nodes?.length) {
      return onFetchTilesError(ERROR_MESSAGES.NO_RESULTS);
    }

    const hydratedTileset = hydrateTileset(
      {
        tilesetData,
        nextNodeId,
        startX: startNode?.x,
        startY: startNode?.y,
        startNodeId: parseInt(startNode?.id, 10),
        generatedWithAI: true,
      },
      wfState,
    );
    onFetchTilesSuccess();
    updateAIGeneratedTiles(hydratedTileset);
  };

  const handleOnError = (err: ApolloError) => {
    onFetchTilesError(err.message);
  };

  /** Fetch generated journey query */
  const [fetchAndInsertGenAIJourney] = useLazyQuery(fetchGenAIJourneys, {
    fetchPolicy: 'no-cache',
    context,
    onCompleted: (res) => handleOnCompleted(res.generateExampleJourneys),
    onError: handleOnError,
  });

  /** Fetch generated tiles query */
  const [fetchAndInsertGenAITileset] = useLazyQuery(fetchGenAITileset, {
    fetchPolicy: 'no-cache',
    context,
    onCompleted: (res) => handleOnCompleted(res.generateTileset),
    onError: handleOnError,
  });

  const handleFetch = useCallback(
    ({ prompt, addOrReplace }: { prompt: string; addOrReplace?: ToggleType }) => {
      if (selectedAINodes.length === 0) {
        // Save initial workflow state to reset to if user resets
        setInitialWfState({ nodes: nodeMap, edges, nextNodeId });
        // Save tiles to connect to since context values change after the tiles are fetched
        setInitialAddOrReplace({
          initialAddToPath: addOrReplace === ToggleType.Add ? addToPathTileId : undefined,
          initialSourceEdges: addOrReplace === ToggleType.Replace ? sourceEdges : undefined,
          initialTargetEdges: addOrReplace === ToggleType.Replace ? targetEdges : undefined,
        });
      }
      if (abortControllerRef.current.signal.aborted) {
        abortControllerRef.current = new AbortController();
      }

      const variables = {
        prompt,
      };

      if (promptType === PromptType.NewTileset) {
        fetchAndInsertGenAITileset({
          variables,
        });
      }
      if (promptType === PromptType.NewJourney) {
        fetchAndInsertGenAIJourney({
          variables,
        });
      }
    },
    [selectedAINodes, nodeMap, edges, nextNodeId, promptType, addToPathTileId],
  );

  const handleCancel = () => {
    abortControllerRef.current.abort();
  };

  return {
    cancelFetchAndInsertGenAITiles: handleCancel,
    fetchAndInsertGenAITiles: handleFetch,
    updateAIGeneratedTiles,
  };
};
